/**
 * Tenants cell content for a single unit row. Lazy-loads the unit's tenants when the
 * row renders (i.e. when the property card is expanded) and shows a one-line skeleton
 * while the request is in flight.
 */
import { useState } from 'react';

import { Box, FilterChip, Heading, Paragraph, Popover, TextButton } from '@zillow/constellation-10';

import { ModalTenantSelect } from '@zg-rentals/modal-tenant';
import { useQueryClient } from '@zg-rentals/react-query5';

import { usePropertyTableTenants } from '../../../hooks/queries/propertyTable';

import { PROXY_PATH } from '../../../constants/urls';

import { TruncatedTextWithTooltip } from '../../../components/TruncatedTextWithTooltip';

import {
  tenantsListStyles,
  tenantsOverflowChipLabelStyles,
  tenantsOverflowChipStyles,
  truncatedTextStyles,
} from '../styles';

import { createZgApiHttpClient } from '../../../clients/zgApiHttpClient';
import SkeletonCell from './SkeletonCell';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

// Show up to this many tenant names inline; the rest collapse into a "+N" popover.
const MAX_VISIBLE_TENANTS = 2;

type Props = {
  listingAlias: ListingAlias;
};

const TenantsCell = ({ listingAlias }: Props) => {
  const queryClient = useQueryClient();
  const { data, isFetching } = usePropertyTableTenants(listingAlias);

  const [isModalTenantSelectOpen, setIsModalTenantSelectOpen] = useState(false);

  if (isFetching) {
    return <SkeletonCell loadingWidth="100px" />;
  }

  const tenants = data?.tenantNames ?? [];
  const visibleTenants = tenants.slice(0, MAX_VISIBLE_TENANTS);
  const overflowTenants = tenants.slice(MAX_VISIBLE_TENANTS);

  // In dev, route through the local proxy so the correct brand header is injected (avoids hotpads 403).
  const client = createZgApiHttpClient(
    process.env.NODE_ENV === 'development' ? `/${PROXY_PATH}` : (process.env.NEXT_PUBLIC_ZG_API_PUBLIC ?? ''),
  );

  if (visibleTenants.length === 0) {
    return (
      <>
        <TextButton onClick={() => setIsModalTenantSelectOpen(true)}>Add tenants</TextButton>

        <ModalTenantSelect
          client={client}
          listingAlias={listingAlias}
          isOpen={isModalTenantSelectOpen}
          queryClient={queryClient}
          onAddRenterSuccess={() => {
            queryClient.invalidateQueries({
              predicate: ({ meta }) =>
                Boolean(meta?.tags?.includes('propertyTableTenants') && meta?.tags?.includes(listingAlias)),
            });
          }}
          onClose={() => setIsModalTenantSelectOpen(false)}
        />
      </>
    );
  }

  return (
    <Box css={tenantsListStyles}>
      <TruncatedTextWithTooltip text={visibleTenants.join(', ')} textStyle="body-sm" css={truncatedTextStyles} />
      {overflowTenants.length > 0 && (
        <Popover
          tone="neutral"
          header={<Heading level={2}>Additional tenants</Heading>}
          body={
            <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
              {overflowTenants.map((name) => (
                <Paragraph key={name} textStyle="body-sm">
                  {name}
                </Paragraph>
              ))}
            </Box>
          }
          trigger={
            <FilterChip.Root aria-label={`Show ${overflowTenants.length} more tenants`} css={tenantsOverflowChipStyles}>
              <FilterChip.Label css={tenantsOverflowChipLabelStyles}>{`+${overflowTenants.length}`}</FilterChip.Label>
            </FilterChip.Root>
          }
        />
      )}
    </Box>
  );
};

export default TenantsCell;
